import { z } from "zod";

import { brandSocialAffinitiesSchema } from "./brand-profile.ts";
import {
  getSocialPostCanvasSize,
  isSafeSocialPostCssColor,
  socialPostImageBackgroundModeSchema,
  socialPostImageFitSchema,
  SOCIAL_POST_TYPES,
  socialPostSceneCanvasV1Schema,
  socialPostSceneFontV1Schema,
  socialPostSceneFrameBlockV1Schema,
  socialPostSceneImageBlockV1Schema,
  socialPostSceneLogoBlockV1Schema,
  socialPostSceneRectBlockV1Schema,
  socialPostSceneShapeBlockV1Schema,
  socialPostSceneTextBlockV1Schema,
  socialPostTextAlignSchema,
  socialPostTypeSchema,
  socialPostVerticalAlignSchema,
} from "./social-post-scene.ts";

const nonEmptyStringSchema = z.string().trim().min(1);
const localTemplateIdentifierSchema = nonEmptyStringSchema
  .max(80)
  .regex(/^[a-z0-9]+(?:[_-][a-z0-9]+)*$/, {
    message:
      "Template-local identifiers must be lowercase slugs using letters, numbers, underscores, or hyphens.",
  });
const templateDescriptionSchema = nonEmptyStringSchema.max(2000);
const colorStringSchema = nonEmptyStringSchema.max(160).refine(isSafeSocialPostCssColor, {
  message: "Color must be a safe CSS color value.",
});
const MAX_IMAGE_SLOT_FILL_COUNT = 6;

export const SOCIAL_POST_LAYOUT_TEMPLATE_VERSION = 2;
export const SOCIAL_POST_LAYOUT_TEMPLATE_EXPORT_BUNDLE_VERSION = 1;
export const SOCIAL_POST_LAYOUT_TEMPLATE_NAME_MAX_LENGTH = 120;
export const SOCIAL_POST_LAYOUT_TEMPLATE_IMAGE_SLOT_PROMPT_MAX_LENGTH = 5000;

const templateNameSchema = nonEmptyStringSchema.max(SOCIAL_POST_LAYOUT_TEMPLATE_NAME_MAX_LENGTH);

export const socialPostLayoutTemplateIdSchema = z
  .uuid()
  .refine((value) => value.toLowerCase() !== "00000000-0000-0000-0000-000000000000", {
    message: "Template ID must be a non-nil UUID.",
  });
export const socialPostLayoutTemplateNameSchema = templateNameSchema;
export type SocialPostLayoutTemplateId = z.infer<typeof socialPostLayoutTemplateIdSchema>;
export type SocialPostLayoutTemplateName = z.infer<typeof socialPostLayoutTemplateNameSchema>;

export const SOCIAL_POST_LAYOUT_TEMPLATE_STATUSES = ["draft", "published", "archived"] as const;
export const socialPostLayoutTemplateStatusSchema = z.enum(SOCIAL_POST_LAYOUT_TEMPLATE_STATUSES);

export const SOCIAL_POST_LAYOUT_TEMPLATE_SLOT_REQUIREMENTS = [
  "required",
  "optional",
  "unsupported",
] as const;
export const socialPostLayoutTemplateSlotRequirementSchema = z.enum(
  SOCIAL_POST_LAYOUT_TEMPLATE_SLOT_REQUIREMENTS,
);

export const SOCIAL_POST_LAYOUT_TEMPLATE_IMAGE_SLOT_KINDS = [
  "hero",
  "supporting",
  "detail",
  "texture",
  "background",
  "product",
  "portrait",
  "custom",
] as const;
export const socialPostLayoutTemplateImageSlotKindSchema = z.enum(
  SOCIAL_POST_LAYOUT_TEMPLATE_IMAGE_SLOT_KINDS,
);

export const SOCIAL_POST_LAYOUT_TEMPLATE_TEXT_SLOT_KINDS = [
  "headline",
  "body",
  "label",
  "quote",
  "attribution",
  "callout",
  "eyebrow",
  "custom",
] as const;
export const socialPostLayoutTemplateTextSlotKindSchema = z.enum(
  SOCIAL_POST_LAYOUT_TEMPLATE_TEXT_SLOT_KINDS,
);

export const SOCIAL_POST_LAYOUT_TEMPLATE_SAFE_ZONE_ROLES = [
  "none",
  "needs_negative_space",
  "background_only",
  "focal_subject",
  "overlay_anchor",
] as const;
export const socialPostLayoutTemplateSafeZoneRoleSchema = z.enum(
  SOCIAL_POST_LAYOUT_TEMPLATE_SAFE_ZONE_ROLES,
);

export const SOCIAL_POST_LAYOUT_TEMPLATE_CONTROL_KINDS = [
  "tone",
  "style",
  "placement",
  "emphasis",
  "custom",
] as const;
export const socialPostLayoutTemplateControlKindSchema = z.enum(
  SOCIAL_POST_LAYOUT_TEMPLATE_CONTROL_KINDS,
);

export const SOCIAL_POST_LAYOUT_TEMPLATE_AI_POLICY_MODES = [
  "locked",
  "content_only",
  "move",
  "resize_move",
  "style",
  "art_directed",
] as const;
export const socialPostLayoutTemplateAiPolicyModeSchema = z.enum(
  SOCIAL_POST_LAYOUT_TEMPLATE_AI_POLICY_MODES,
);

export const SOCIAL_POST_LAYOUT_TEMPLATE_BRAND_COLOR_BIASES = [
  "required",
  "strong",
  "soft",
] as const;
export const socialPostLayoutTemplateBrandColorBiasSchema = z.enum(
  SOCIAL_POST_LAYOUT_TEMPLATE_BRAND_COLOR_BIASES,
);

export const SOCIAL_POST_LAYOUT_TEMPLATE_PATCH_FIELDS = [
  "x",
  "y",
  "width",
  "height",
  "rotation",
  "visible",
  "opacity",
  "fill",
  "stroke",
  "strokeWidth",
  "cornerRadius",
  "fontSize",
  "lineHeight",
  "letterSpacing",
  "align",
  "verticalAlign",
  "fit",
] as const;
export const socialPostLayoutTemplatePatchFieldSchema = z.enum(
  SOCIAL_POST_LAYOUT_TEMPLATE_PATCH_FIELDS,
);

const MOVE_PATCH_FIELDS = ["x", "y"] as const;
const RESIZE_MOVE_PATCH_FIELDS = ["x", "y", "width", "height", "rotation"] as const;
const STYLE_PATCH_FIELDS = [
  "visible",
  "opacity",
  "fill",
  "stroke",
  "strokeWidth",
  "cornerRadius",
  "fontSize",
  "lineHeight",
  "letterSpacing",
  "align",
  "verticalAlign",
  "fit",
] as const;

export const LAYOUT_TEMPLATE_PATCH_FIELDS_BY_AI_POLICY_MODE = {
  locked: [],
  content_only: [],
  move: MOVE_PATCH_FIELDS,
  resize_move: RESIZE_MOVE_PATCH_FIELDS,
  style: STYLE_PATCH_FIELDS,
  art_directed: SOCIAL_POST_LAYOUT_TEMPLATE_PATCH_FIELDS,
} as const satisfies Record<
  (typeof SOCIAL_POST_LAYOUT_TEMPLATE_AI_POLICY_MODES)[number],
  readonly (typeof SOCIAL_POST_LAYOUT_TEMPLATE_PATCH_FIELDS)[number][]
>;

const TEXT_BLOCK_PATCH_FIELDS = [
  "x",
  "y",
  "width",
  "height",
  "rotation",
  "visible",
  "opacity",
  "fill",
  "fontSize",
  "lineHeight",
  "letterSpacing",
  "align",
  "verticalAlign",
] as const;
const IMAGE_BLOCK_PATCH_FIELDS = [
  "x",
  "y",
  "width",
  "height",
  "rotation",
  "visible",
  "opacity",
  "fit",
  "cornerRadius",
] as const;
const LOGO_BLOCK_PATCH_FIELDS = IMAGE_BLOCK_PATCH_FIELDS;
const RECT_BLOCK_PATCH_FIELDS = [
  "x",
  "y",
  "width",
  "height",
  "rotation",
  "visible",
  "opacity",
  "fill",
  "stroke",
  "strokeWidth",
  "cornerRadius",
] as const;
const SHAPE_BLOCK_PATCH_FIELDS = RECT_BLOCK_PATCH_FIELDS;
const FRAME_BLOCK_PATCH_FIELDS = RECT_BLOCK_PATCH_FIELDS;

export const LAYOUT_TEMPLATE_PATCH_FIELDS_BY_BLOCK_TYPE = {
  text: TEXT_BLOCK_PATCH_FIELDS,
  image: IMAGE_BLOCK_PATCH_FIELDS,
  logo: LOGO_BLOCK_PATCH_FIELDS,
  rect: RECT_BLOCK_PATCH_FIELDS,
  shape: SHAPE_BLOCK_PATCH_FIELDS,
  frame: FRAME_BLOCK_PATCH_FIELDS,
} as const;

const boundedRectSchema = z
  .object({
    x: z.number().finite().nonnegative(),
    y: z.number().finite().nonnegative(),
    width: z.number().finite().positive(),
    height: z.number().finite().positive(),
  })
  .strict();

export const socialPostLayoutTemplateBoundsSchema = z.union([
  z.enum(["canvas", "safe_area", "image_safe_area"]),
  boundedRectSchema,
]);

export const socialPostLayoutTemplateAiPolicySchema = z
  .object({
    mode: socialPostLayoutTemplateAiPolicyModeSchema,
    allowedFields: z.array(socialPostLayoutTemplatePatchFieldSchema).max(32).default([]),
    bounds: socialPostLayoutTemplateBoundsSchema.optional(),
    allowHideWhenOptional: z.boolean().default(false),
    minWidth: z.number().finite().positive().optional(),
    minHeight: z.number().finite().positive().optional(),
    maxWidth: z.number().finite().positive().optional(),
    maxHeight: z.number().finite().positive().optional(),
    brandColorBias: socialPostLayoutTemplateBrandColorBiasSchema.default("strong"),
  })
  .strict()
  .superRefine((policy, ctx) => {
    const allowedForMode = new Set(LAYOUT_TEMPLATE_PATCH_FIELDS_BY_AI_POLICY_MODE[policy.mode]);
    for (const [index, field] of policy.allowedFields.entries()) {
      if (!allowedForMode.has(field)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["allowedFields", index],
          message: `${field} is not allowed for ${policy.mode} AI policy.`,
        });
      }
    }

    if (
      policy.maxWidth !== undefined &&
      policy.minWidth !== undefined &&
      policy.maxWidth < policy.minWidth
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["maxWidth"],
        message: "maxWidth cannot be smaller than minWidth.",
      });
    }

    if (
      policy.maxHeight !== undefined &&
      policy.minHeight !== undefined &&
      policy.maxHeight < policy.minHeight
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["maxHeight"],
        message: "maxHeight cannot be smaller than minHeight.",
      });
    }
  });

export const socialPostLayoutTemplateLayerBindingSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("textSlot"), slotId: localTemplateIdentifierSchema }).strict(),
  z
    .object({
      type: z.literal("imageSlot"),
      slotId: localTemplateIdentifierSchema,
      index: z
        .number()
        .int()
        .nonnegative()
        .max(MAX_IMAGE_SLOT_FILL_COUNT - 1),
    })
    .strict(),
  z.object({ type: z.literal("logoSlot") }).strict(),
  z.object({ type: z.literal("static") }).strict(),
]);

const templateLayerMetadataSchema = z.object({
  binding: socialPostLayoutTemplateLayerBindingSchema.default({ type: "static" }),
  optional: z.boolean().default(false),
  aiPolicy: socialPostLayoutTemplateAiPolicySchema.default({
    mode: "locked",
    allowedFields: [],
    allowHideWhenOptional: false,
    brandColorBias: "strong",
  }),
  safeArea: socialPostLayoutTemplateBoundsSchema.optional(),
});

const socialPostLayoutTemplateTextLayerSchema = socialPostSceneTextBlockV1Schema.extend(
  templateLayerMetadataSchema.shape,
);
const socialPostLayoutTemplateImageLayerSchema = socialPostSceneImageBlockV1Schema.extend(
  templateLayerMetadataSchema.shape,
);
const socialPostLayoutTemplateLogoLayerSchema = socialPostSceneLogoBlockV1Schema.extend(
  templateLayerMetadataSchema.shape,
);
const socialPostLayoutTemplateRectLayerSchema = socialPostSceneRectBlockV1Schema.extend(
  templateLayerMetadataSchema.shape,
);
const socialPostLayoutTemplateShapeLayerSchema = socialPostSceneShapeBlockV1Schema.extend(
  templateLayerMetadataSchema.shape,
);
const socialPostLayoutTemplateFrameLayerSchema = socialPostSceneFrameBlockV1Schema.extend(
  templateLayerMetadataSchema.shape,
);

export const socialPostLayoutTemplateLayerSchema = z.discriminatedUnion("type", [
  socialPostLayoutTemplateTextLayerSchema,
  socialPostLayoutTemplateImageLayerSchema,
  socialPostLayoutTemplateLogoLayerSchema,
  socialPostLayoutTemplateRectLayerSchema,
  socialPostLayoutTemplateShapeLayerSchema,
  socialPostLayoutTemplateFrameLayerSchema,
]);

export const socialPostLayoutTemplateImageSlotSchema = z
  .object({
    id: localTemplateIdentifierSchema,
    label: nonEmptyStringSchema.max(120),
    kind: socialPostLayoutTemplateImageSlotKindSchema,
    requirement: socialPostLayoutTemplateSlotRequirementSchema,
    minCount: z.number().int().min(0).max(MAX_IMAGE_SLOT_FILL_COUNT),
    maxCount: z.number().int().min(0).max(MAX_IMAGE_SLOT_FILL_COUNT),
    purpose: nonEmptyStringSchema.max(500),
    safeZoneRole: socialPostLayoutTemplateSafeZoneRoleSchema.default("none"),
  })
  .strict()
  .superRefine((slot, ctx) => {
    if (slot.minCount > slot.maxCount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["minCount"],
        message: "Image slot minCount cannot exceed maxCount.",
      });
    }

    if (slot.requirement === "required" && slot.minCount < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["minCount"],
        message: "Required image slots must require at least one image.",
      });
    }

    if (slot.requirement === "unsupported" && slot.maxCount > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["maxCount"],
        message: "Unsupported image slots cannot accept images.",
      });
    }
  });

export const socialPostLayoutTemplateTextSlotSchema = z
  .object({
    id: localTemplateIdentifierSchema,
    label: nonEmptyStringSchema.max(120),
    kind: socialPostLayoutTemplateTextSlotKindSchema,
    requirement: socialPostLayoutTemplateSlotRequirementSchema,
    purpose: nonEmptyStringSchema.max(500),
    suggestedMaxLength: z.number().int().min(0).max(1000).optional(),
    toneHint: nonEmptyStringSchema.max(200).optional(),
  })
  .strict();

export const socialPostLayoutTemplateLogoSlotSchema = z
  .object({
    requirement: socialPostLayoutTemplateSlotRequirementSchema,
    purpose: nonEmptyStringSchema.max(500),
    preferredPlacements: z
      .array(z.enum(["top_left", "top_right", "bottom_left", "bottom_right"]))
      .max(4)
      .default([]),
  })
  .strict();

export const socialPostLayoutTemplateSlotsSchema = z
  .object({
    imageSlots: z.array(socialPostLayoutTemplateImageSlotSchema).min(1).max(12),
    textSlots: z.array(socialPostLayoutTemplateTextSlotSchema).min(1).max(16),
    logoSlot: socialPostLayoutTemplateLogoSlotSchema,
  })
  .strict();

export const socialPostLayoutTemplateControlValueSchema = z
  .object({
    id: localTemplateIdentifierSchema,
    label: nonEmptyStringSchema.max(120),
  })
  .strict();

export const socialPostLayoutTemplateControlSchema = z
  .object({
    id: localTemplateIdentifierSchema,
    label: nonEmptyStringSchema.max(120),
    kind: socialPostLayoutTemplateControlKindSchema,
    values: z.array(socialPostLayoutTemplateControlValueSchema).min(1).max(16),
    defaultValue: localTemplateIdentifierSchema.optional(),
    purpose: nonEmptyStringSchema.max(500),
  })
  .strict()
  .superRefine((control, ctx) => {
    const values = new Set(control.values.map((value) => value.id));
    if (values.size !== control.values.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["values"],
        message: "Control values must be unique.",
      });
    }

    if (control.defaultValue && !values.has(control.defaultValue)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["defaultValue"],
        message: "Control defaultValue must reference one of its values.",
      });
    }
  });

export const socialPostLayoutTemplatePaletteHintsSchema = z
  .object({
    brandColorBias: socialPostLayoutTemplateBrandColorBiasSchema.default("strong"),
    preferredColors: z.array(colorStringSchema).max(16).default([]),
    avoidColors: z.array(colorStringSchema).max(16).default([]),
  })
  .strict();

export const socialPostLayoutTemplateSceneSchema = z
  .object({
    canvas: socialPostSceneCanvasV1Schema,
    fonts: z.array(socialPostSceneFontV1Schema).max(2),
    layers: z.array(socialPostLayoutTemplateLayerSchema).min(1).max(32),
  })
  .strict();

const imageSlotFillSchema = z
  .object({
    prompt: nonEmptyStringSchema.max(SOCIAL_POST_LAYOUT_TEMPLATE_IMAGE_SLOT_PROMPT_MAX_LENGTH),
    backgroundMode: socialPostImageBackgroundModeSchema.default("opaque"),
  })
  .strict();

export const socialPostLayoutTemplatePreviewFillSchema = z
  .object({
    text: z.record(localTemplateIdentifierSchema, nonEmptyStringSchema.max(1000)).default({}),
    images: z
      .record(
        localTemplateIdentifierSchema,
        z.array(imageSlotFillSchema).max(MAX_IMAGE_SLOT_FILL_COUNT),
      )
      .default({}),
    controls: z.record(localTemplateIdentifierSchema, localTemplateIdentifierSchema).default({}),
    includeLogo: z.boolean().default(false),
  })
  .strict();

export const socialPostLayoutTemplateValidationRulesSchema = z
  .object({
    requireAllRequiredSlotsBound: z.boolean().default(true),
    maxTextBlocks: z.number().int().min(1).max(16).default(6),
    minImageVisibleRatio: z.number().finite().min(0).max(1).default(0.5),
  })
  .strict();

export const socialPostLayoutTemplateSelectionMetadataSchema = z
  .object({
    bestFor: z.array(nonEmptyStringSchema.max(300)).min(1).max(16),
    avoidWhen: z.array(nonEmptyStringSchema.max(300)).min(1).max(16),
    socialAffinities: brandSocialAffinitiesSchema.optional(),
  })
  .strict();

const getDuplicateValues = (values: readonly string[]): string[] => {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  for (const value of values) {
    if (seen.has(value)) {
      duplicates.add(value);
    }
    seen.add(value);
  }

  return [...duplicates];
};

const getBoundLayerCount = (
  layers: readonly z.infer<typeof socialPostLayoutTemplateLayerSchema>[],
  bindingType: "textSlot" | "imageSlot" | "logoSlot",
  slotId?: string,
): number =>
  layers.filter((layer) => {
    if (layer.binding.type !== bindingType) {
      return false;
    }

    return slotId === undefined || ("slotId" in layer.binding && layer.binding.slotId === slotId);
  }).length;

const getBoundImageSlotIndexes = (
  layers: readonly z.infer<typeof socialPostLayoutTemplateLayerSchema>[],
  slotId: string,
): Set<number> =>
  new Set(
    layers.flatMap((layer) =>
      layer.binding.type === "imageSlot" && layer.binding.slotId === slotId
        ? [layer.binding.index]
        : [],
    ),
  );

const hasOptionalRequiredBinding = (
  layers: readonly z.infer<typeof socialPostLayoutTemplateLayerSchema>[],
  bindingType: "textSlot" | "imageSlot" | "logoSlot",
  slotId?: string,
): boolean =>
  layers.some((layer) => {
    if (!layer.optional || layer.binding.type !== bindingType) {
      return false;
    }

    return slotId === undefined || ("slotId" in layer.binding && layer.binding.slotId === slotId);
  });

const hasInvisibleRequiredBinding = (
  layers: readonly z.infer<typeof socialPostLayoutTemplateLayerSchema>[],
  bindingType: "textSlot" | "imageSlot" | "logoSlot",
  slotId?: string,
): boolean =>
  layers.some((layer) => {
    if (layer.binding.type !== bindingType || (layer.visible && layer.opacity > 0)) {
      return false;
    }

    return slotId === undefined || ("slotId" in layer.binding && layer.binding.slotId === slotId);
  });

const assertIdsUnique = (
  ids: readonly string[],
  ctx: z.RefinementCtx,
  path: Array<string | number>,
  label: string,
) => {
  for (const duplicate of getDuplicateValues(ids)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path,
      message: `${label} ids must be unique: ${duplicate}.`,
    });
  }
};

export const socialPostLayoutTemplateDefinitionSchema = z
  .object({
    type: z.literal("social_post.layout_template"),
    version: z.literal(SOCIAL_POST_LAYOUT_TEMPLATE_VERSION),
    id: socialPostLayoutTemplateIdSchema,
    name: templateNameSchema,
    description: templateDescriptionSchema,
    status: socialPostLayoutTemplateStatusSchema.default("draft"),
    canonicalPostType: z.literal("feed_portrait"),
    supportedPostTypes: z.array(socialPostTypeSchema).min(1).max(SOCIAL_POST_TYPES.length),
    selection: socialPostLayoutTemplateSelectionMetadataSchema,
    slots: socialPostLayoutTemplateSlotsSchema,
    controls: z.array(socialPostLayoutTemplateControlSchema).max(16).default([]),
    paletteHints: socialPostLayoutTemplatePaletteHintsSchema.default({
      brandColorBias: "strong",
      preferredColors: [],
      avoidColors: [],
    }),
    canonicalScene: socialPostLayoutTemplateSceneSchema,
    previewFill: socialPostLayoutTemplatePreviewFillSchema.default({
      text: {},
      images: {},
      controls: {},
      includeLogo: false,
    }),
    validation: socialPostLayoutTemplateValidationRulesSchema.default({
      requireAllRequiredSlotsBound: true,
      maxTextBlocks: 6,
      minImageVisibleRatio: 0.5,
    }),
  })
  .strict()
  .superRefine((template, ctx) => {
    if (!template.supportedPostTypes.includes(template.canonicalPostType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["supportedPostTypes"],
        message: "supportedPostTypes must include the canonical feed_portrait post type.",
      });
    }

    assertIdsUnique(
      template.supportedPostTypes,
      ctx,
      ["supportedPostTypes"],
      "Supported post type",
    );

    assertIdsUnique(
      [
        ...template.slots.imageSlots.map((slot) => slot.id),
        ...template.slots.textSlots.map((slot) => slot.id),
      ],
      ctx,
      ["slots"],
      "Slot",
    );
    assertIdsUnique(
      template.controls.map((control) => control.id),
      ctx,
      ["controls"],
      "Control",
    );
    assertIdsUnique(
      template.canonicalScene.layers.map((layer) => layer.id),
      ctx,
      ["canonicalScene", "layers"],
      "Layer",
    );

    const textSlotsById = new Map(template.slots.textSlots.map((slot) => [slot.id, slot]));
    const imageSlotsById = new Map(template.slots.imageSlots.map((slot) => [slot.id, slot]));
    const controlsById = new Map(template.controls.map((control) => [control.id, control]));
    const fontFamilies = new Set(template.canonicalScene.fonts.map((font) => font.family));
    const canonicalCanvasSize = getSocialPostCanvasSize("instagram", template.canonicalPostType);
    let logoLayerCount = 0;

    for (const slotId of Object.keys(template.previewFill.text)) {
      const textSlot = textSlotsById.get(slotId);
      if (!textSlot) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["previewFill", "text", slotId],
          message: "Preview text fill references an unknown text slot.",
        });
      } else if (textSlot.requirement === "unsupported") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["previewFill", "text", slotId],
          message: "Preview text fill references an unsupported text slot.",
        });
      }
    }

    for (const [slotId, fills] of Object.entries(template.previewFill.images)) {
      const imageSlot = imageSlotsById.get(slotId);
      if (!imageSlot) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["previewFill", "images", slotId],
          message: "Preview image fill references an unknown image slot.",
        });
      } else if (imageSlot.requirement === "unsupported") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["previewFill", "images", slotId],
          message: "Preview image fill references an unsupported image slot.",
        });
      } else if (fills.length > imageSlot.maxCount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["previewFill", "images", slotId],
          message: "Preview image fill count cannot exceed the referenced image slot maxCount.",
        });
      } else if (fills.length > 0 && fills.length < imageSlot.minCount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["previewFill", "images", slotId],
          message:
            "Preview image fill count cannot be less than the referenced image slot minCount.",
        });
      }
    }

    for (const [controlId, valueId] of Object.entries(template.previewFill.controls)) {
      const control = controlsById.get(controlId);
      if (!control) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["previewFill", "controls", controlId],
          message: "Preview control fill references an unknown control.",
        });
      } else if (!control.values.some((value) => value.id === valueId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["previewFill", "controls", controlId],
          message: "Preview control fill must reference one of the control values.",
        });
      }
    }

    if (canonicalCanvasSize) {
      if (template.canonicalScene.canvas.width !== canonicalCanvasSize.width) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["canonicalScene", "canvas", "width"],
          message: `Canonical canvas width must be ${canonicalCanvasSize.width} for ${template.canonicalPostType}.`,
        });
      }

      if (template.canonicalScene.canvas.height !== canonicalCanvasSize.height) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["canonicalScene", "canvas", "height"],
          message: `Canonical canvas height must be ${canonicalCanvasSize.height} for ${template.canonicalPostType}.`,
        });
      }
    }

    for (const [index, layer] of template.canonicalScene.layers.entries()) {
      if (layer.type === "logo") {
        logoLayerCount += 1;
        if (layer.binding.type !== "logoSlot") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalScene", "layers", index, "binding"],
            message: "Logo layers must use logoSlot bindings.",
          });
        }
      }

      const blockFields = new Set(LAYOUT_TEMPLATE_PATCH_FIELDS_BY_BLOCK_TYPE[layer.type]);
      for (const field of layer.aiPolicy.allowedFields) {
        if (!blockFields.has(field)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalScene", "layers", index, "aiPolicy", "allowedFields"],
            message: `${field} cannot be patched on ${layer.type} layers.`,
          });
        }
      }

      if (layer.aiPolicy.allowedFields.includes("visible")) {
        if (!layer.optional || !layer.aiPolicy.allowHideWhenOptional) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalScene", "layers", index, "aiPolicy", "allowedFields"],
            message: "visible can only be AI-patched on optional layers that allow hide/show.",
          });
        }
      }

      if (layer.type === "text" && !fontFamilies.has(layer.fontFamily)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["canonicalScene", "layers", index, "fontFamily"],
          message: "Text layers must reference a declared scene font.",
        });
      }

      if (layer.binding.type === "textSlot") {
        if (layer.type !== "text") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalScene", "layers", index, "binding"],
            message: "textSlot bindings can only be assigned to text layers.",
          });
        }
        const textSlot = textSlotsById.get(layer.binding.slotId);
        if (!textSlot) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalScene", "layers", index, "binding", "slotId"],
            message: "textSlot binding references an unknown text slot.",
          });
        } else if (textSlot.requirement === "unsupported") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalScene", "layers", index, "binding", "slotId"],
            message: "textSlot binding references an unsupported text slot.",
          });
        }
      }

      if (layer.binding.type === "imageSlot") {
        if (layer.type !== "image") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalScene", "layers", index, "binding"],
            message: "imageSlot bindings can only be assigned to image layers.",
          });
        }
        const imageSlot = imageSlotsById.get(layer.binding.slotId);
        if (!imageSlot) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalScene", "layers", index, "binding", "slotId"],
            message: "imageSlot binding references an unknown image slot.",
          });
        } else if (layer.binding.index >= imageSlot.maxCount) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalScene", "layers", index, "binding", "index"],
            message:
              "imageSlot binding index must be less than the referenced image slot maxCount.",
          });
        }
      }

      if (layer.binding.type === "logoSlot") {
        if (layer.type !== "logo") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalScene", "layers", index, "binding"],
            message: "logoSlot bindings can only be assigned to logo layers.",
          });
        }
        if (template.slots.logoSlot.requirement === "unsupported") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalScene", "layers", index, "binding"],
            message: "logoSlot bindings are not allowed when the logo slot is unsupported.",
          });
        }
      }
    }

    if (logoLayerCount > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["canonicalScene", "layers"],
        message: "Templates support at most one logo layer.",
      });
    }

    if (template.validation.requireAllRequiredSlotsBound) {
      for (const slot of template.slots.imageSlots) {
        if (slot.requirement !== "required") {
          continue;
        }

        if (
          getBoundImageSlotIndexes(template.canonicalScene.layers, slot.id).size < slot.minCount
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["slots", "imageSlots"],
            message: `Required image slot ${slot.id} needs at least ${slot.minCount} distinct bound image index(es).`,
          });
        }

        if (hasOptionalRequiredBinding(template.canonicalScene.layers, "imageSlot", slot.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalScene", "layers"],
            message: `Required image slot ${slot.id} cannot depend on optional layers.`,
          });
        }

        if (hasInvisibleRequiredBinding(template.canonicalScene.layers, "imageSlot", slot.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalScene", "layers"],
            message: `Required image slot ${slot.id} cannot depend on hidden or transparent layers.`,
          });
        }
      }

      for (const slot of template.slots.textSlots) {
        if (slot.requirement !== "required") {
          continue;
        }

        if (getBoundLayerCount(template.canonicalScene.layers, "textSlot", slot.id) < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["slots", "textSlots"],
            message: `Required text slot ${slot.id} needs a bound text layer.`,
          });
        }

        if (hasOptionalRequiredBinding(template.canonicalScene.layers, "textSlot", slot.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalScene", "layers"],
            message: `Required text slot ${slot.id} cannot depend on optional layers.`,
          });
        }

        if (hasInvisibleRequiredBinding(template.canonicalScene.layers, "textSlot", slot.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalScene", "layers"],
            message: `Required text slot ${slot.id} cannot depend on hidden or transparent layers.`,
          });
        }
      }

      if (
        template.slots.logoSlot.requirement === "required" &&
        getBoundLayerCount(template.canonicalScene.layers, "logoSlot") < 1
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["slots", "logoSlot"],
          message: "Required logo slot needs a bound logo layer.",
        });
      }

      if (
        template.slots.logoSlot.requirement === "required" &&
        hasOptionalRequiredBinding(template.canonicalScene.layers, "logoSlot")
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["canonicalScene", "layers"],
          message: "Required logo slot cannot depend on optional layers.",
        });
      }

      if (
        template.slots.logoSlot.requirement === "required" &&
        hasInvisibleRequiredBinding(template.canonicalScene.layers, "logoSlot")
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["canonicalScene", "layers"],
          message: "Required logo slot cannot depend on hidden or transparent layers.",
        });
      }
    }
  });

export type SocialPostLayoutTemplateDefinitionV2 = z.infer<
  typeof socialPostLayoutTemplateDefinitionSchema
>;
export type SocialPostLayoutTemplateLayerV2 = z.infer<typeof socialPostLayoutTemplateLayerSchema>;
export type SocialPostLayoutTemplateAiPolicyV2 = z.infer<
  typeof socialPostLayoutTemplateAiPolicySchema
>;
export type SocialPostLayoutTemplatePatchField = z.infer<
  typeof socialPostLayoutTemplatePatchFieldSchema
>;

export const socialPostLayoutTemplateExportRevisionSchema = z
  .object({
    revisionNumber: z.number().int().min(1).max(10_000),
    template: socialPostLayoutTemplateDefinitionSchema,
    publishedAt: z.string().datetime(),
  })
  .strict()
  .superRefine((revision, ctx) => {
    if (revision.template.status !== "published") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["template", "status"],
        message: "Exported revision templates must be published snapshots.",
      });
    }
  });

export const socialPostLayoutTemplateExportItemSchema = z
  .object({
    template: socialPostLayoutTemplateDefinitionSchema,
    status: socialPostLayoutTemplateStatusSchema,
    publishedTemplate: socialPostLayoutTemplateDefinitionSchema.nullable(),
    publishedRevisionNumber: z.number().int().min(1).max(10_000).nullable(),
    sortOrder: z.number().int().min(0).max(10_000),
    revisions: z.array(socialPostLayoutTemplateExportRevisionSchema),
  })
  .strict()
  .superRefine((item, ctx) => {
    if (item.template.status !== item.status) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["template", "status"],
        message: "Exported template status must match the row status.",
      });
    }

    if (item.publishedTemplate && item.publishedTemplate.status !== "published") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["publishedTemplate", "status"],
        message: "Published template snapshots must use published status.",
      });
    }

    if (item.status === "published" && !item.publishedTemplate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["publishedTemplate"],
        message: "Published templates must include a published snapshot.",
      });
    }

    if (item.status === "published" && item.publishedRevisionNumber === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["publishedRevisionNumber"],
        message: "Published templates must include a published revision number.",
      });
    }

    if (!item.publishedTemplate && item.publishedRevisionNumber !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["publishedRevisionNumber"],
        message: "Published revision numbers require a published snapshot.",
      });
    }
  });

export const socialPostLayoutTemplateExportBundleSchema = z
  .object({
    type: z.literal("social_post.layout_template_bundle"),
    version: z.literal(SOCIAL_POST_LAYOUT_TEMPLATE_EXPORT_BUNDLE_VERSION),
    exportedAt: z.string().datetime(),
    templates: z.array(socialPostLayoutTemplateExportItemSchema),
  })
  .strict();

export type SocialPostLayoutTemplateExportRevision = z.infer<
  typeof socialPostLayoutTemplateExportRevisionSchema
>;
export type SocialPostLayoutTemplateExportItem = z.infer<
  typeof socialPostLayoutTemplateExportItemSchema
>;
export type SocialPostLayoutTemplateExportBundle = z.infer<
  typeof socialPostLayoutTemplateExportBundleSchema
>;

export const socialPostLayoutTemplateTextSlotFillSchema = nonEmptyStringSchema.max(1000);
export const socialPostLayoutTemplateImageSlotFillSchema = imageSlotFillSchema;

export const socialPostLayoutTemplatePatchSetSchema = z
  .object({
    x: z.number().finite().optional(),
    y: z.number().finite().optional(),
    width: z.number().finite().positive().optional(),
    height: z.number().finite().positive().optional(),
    rotation: z.number().finite().optional(),
    visible: z.boolean().optional(),
    opacity: z.number().finite().min(0).max(1).optional(),
    fill: colorStringSchema.optional(),
    stroke: colorStringSchema.optional(),
    strokeWidth: z.number().finite().nonnegative().optional(),
    cornerRadius: z.number().finite().nonnegative().optional(),
    fontSize: z.number().finite().positive().optional(),
    lineHeight: z.number().finite().positive().optional(),
    letterSpacing: z.number().finite().optional(),
    align: socialPostTextAlignSchema.optional(),
    verticalAlign: socialPostVerticalAlignSchema.optional(),
    fit: socialPostImageFitSchema.optional(),
  })
  .strict()
  .superRefine((set, ctx) => {
    if (Object.values(set).every((value) => value === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Layer patch set must include at least one field.",
      });
    }
  });

export const socialPostLayoutTemplateLayerPatchSchema = z
  .object({
    blockId: nonEmptyStringSchema.max(120),
    set: socialPostLayoutTemplatePatchSetSchema,
    rationale: nonEmptyStringSchema.max(500).optional(),
  })
  .strict();

export type SocialPostLayoutTemplateLayerPatchV2 = z.infer<
  typeof socialPostLayoutTemplateLayerPatchSchema
>;

export const socialPostLayoutTemplateSafeZoneHintSchema = z
  .object({
    imageSlotId: localTemplateIdentifierSchema,
    avoidOverlayZone: boundedRectSchema,
    reason: nonEmptyStringSchema.max(500),
  })
  .strict();

export const socialPostLayoutTemplatePlanPatchSchema = z
  .object({
    templateId: socialPostLayoutTemplateIdSchema,
    templateName: templateNameSchema,
    targetPostType: socialPostTypeSchema,
    textSlotFills: z.record(
      localTemplateIdentifierSchema,
      socialPostLayoutTemplateTextSlotFillSchema,
    ),
    imageSlotFills: z.record(
      localTemplateIdentifierSchema,
      z.array(socialPostLayoutTemplateImageSlotFillSchema).max(MAX_IMAGE_SLOT_FILL_COUNT),
    ),
    controlValues: z
      .record(localTemplateIdentifierSchema, localTemplateIdentifierSchema)
      .default({}),
    layerPatches: z.array(socialPostLayoutTemplateLayerPatchSchema).max(32).default([]),
    safeZoneHints: z.array(socialPostLayoutTemplateSafeZoneHintSchema).max(12).default([]),
  })
  .strict();

export type SocialPostLayoutTemplatePlanPatchV2 = z.infer<
  typeof socialPostLayoutTemplatePlanPatchSchema
>;

export const getSocialPostLayoutTemplateLayerPatchFields = (
  patch: SocialPostLayoutTemplateLayerPatchV2,
): SocialPostLayoutTemplatePatchField[] =>
  Object.entries(patch.set)
    .filter(([, value]) => value !== undefined)
    .map(([field]) => socialPostLayoutTemplatePatchFieldSchema.parse(field));

export const collectSocialPostLayoutTemplateDefinitionIssues = (value: unknown): string[] => {
  const result = socialPostLayoutTemplateDefinitionSchema.safeParse(value);
  return result.success ? [] : result.error.issues.map((issue) => issue.message);
};
